import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY
);

// Create the UserContext
const UserContext = createContext({
  user: null,
  plan: 'free',
  usage: {},
  initialLoading: false,
  authOperationLoading: false,
  isGuest: true,
  logout: async () => {},
  getToken: async () => null,
  updatePlan: () => {},
  updateUsage: () => {},
  handleGoogleSignIn: async () => {},
});

// Custom hook to use the UserContext
export const useUser = () => useContext(UserContext);

// UserProvider Component
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [plan, setPlan] = useState('free');
  const [usage, setUsage] = useState(() => {
    // Initialize usage from localStorage for guest users
    return JSON.parse(localStorage.getItem('guestUsage') || '{}');
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [authOperationLoading, setAuthOperationLoading] = useState(false);
  const [tokenCache, setTokenCache] = useState(null);
  const [isGuest, setIsGuest] = useState(true);

  const GUEST_LIMITS = { questions: 5, summaries: 3, ppt: 2 }; // Define guest limits

  const fetchUserData = useCallback(async (currentUser) => {
    console.log("UserContext: fetchUserData called for user:", currentUser?.id || 'none');
    if (!currentUser) {
      setPlan('free');
      setUsage(JSON.parse(localStorage.getItem('guestUsage') || '{}'));
      setIsGuest(true);
      return;
    }

    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('plan')
        .eq('id', currentUser.id)
        .single();

      if (userError && userError.code !== 'PGRST116') {
        console.error('UserContext: Error fetching user plan:', userError);
        setPlan('free');
      } else {
        setPlan(userData?.plan || 'free');
        console.log("UserContext: User plan set to:", userData?.plan || 'free');
      }

      const { data: usageData, error: usageError } = await supabase
        .from('usage_limits')
        .select('feature, used_count, reset_at')
        .eq('user_id', currentUser.id);

      if (usageError && usageError.code !== 'PGRST116') {
        console.error('UserContext: Error fetching usage limits:', usageError);
        setUsage(JSON.parse(localStorage.getItem('guestUsage') || '{}'));
      } else {
        const usageMap = usageData ? usageData.reduce((acc, item) => ({
          ...acc,
          [item.feature]: { used_count: item.used_count, reset_at: item.reset_at }
        }), {}) : {};
        setUsage(usageMap);
        console.log("UserContext: Usage data set:", usageMap);
        localStorage.removeItem('guestUsage'); // Clear guest usage after login
      }
    } catch (error) {
      console.error('UserContext: Critical error in fetchUserData:', error);
      setPlan('free');
      setUsage(JSON.parse(localStorage.getItem('guestUsage') || '{}'));
    }
  }, []);

  useEffect(() => {
    console.log("UserContext: Initiating session fetch...");
    const timeoutId = setTimeout(() => {
      console.warn("UserContext: Initial session fetch timed out after 5s.");
      setInitialLoading(false);
    }, 5000);

    const setupAuthAndFetchSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error("UserContext: Error getting initial session:", error);
          setUser(null);
          setPlan('free');
          setUsage(JSON.parse(localStorage.getItem('guestUsage') || '{}'));
          setTokenCache(null);
          setIsGuest(true);
        } else if (session?.user) {
          console.log("UserContext: Initial session found. User ID:", session.user.id);
          setUser(session.user);
          setTokenCache(session.access_token || null);
          setIsGuest(false);
          await fetchUserData(session.user);
        } else {
          console.log("UserContext: No active session found initially.");
          setUser(null);
          setPlan('free');
          setUsage(JSON.parse(localStorage.getItem('guestUsage') || '{}'));
          setTokenCache(null);
          setIsGuest(true);
        }
      } catch (error) {
        console.error("UserContext: Critical error during initial session fetch:", error);
        setUser(null);
        setPlan('free');
        setUsage(JSON.parse(localStorage.getItem('guestUsage') || '{}'));
        setTokenCache(null);
        setIsGuest(true);
      } finally {
        clearTimeout(timeoutId);
        setInitialLoading(false);
        console.log("UserContext: Initial session fetch complete. Loading:", false);
      }

      const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log(`UserContext: Auth state change event: ${event}`);
        const currentUser = session?.user || null;
        setUser(currentUser);
        setTokenCache(session?.access_token || null);
        setIsGuest(!currentUser);
        if (currentUser) {
          await fetchUserData(currentUser);
        } else {
          setPlan('free');
          setUsage(JSON.parse(localStorage.getItem('guestUsage') || '{}'));
          setTokenCache(null);
        }
      });

      return () => {
        console.log("UserContext: Cleaning up auth listener.");
        authListener.subscription?.unsubscribe();
      };
    };

    setupAuthAndFetchSession();

    return () => {
      console.log("UserContext: Cleaning up effect.");
      clearTimeout(timeoutId);
    };
  }, [fetchUserData]);
  
  const handleGoogleSignIn = async () => {
    console.log("UserContext: Attempting Google sign-in...");
    setAuthOperationLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          // This URL must match the one configured in your Supabase Auth settings
          // and should point back to your app's sign-in page to handle the callback.
          redirectTo: window.location.origin + window.location.pathname,
        },
      });

      if (error) {
        console.error('UserContext: Supabase Google OAuth error:', error);
        throw error;
      }

      // Supabase will handle the redirection, so no further action is needed here.
    } catch (err) {
      console.error('UserContext: Error during Google OAuth:', err);
      throw err;
    } finally {
      setAuthOperationLoading(false);
    }
  };

  const logout = async () => {
    console.log("UserContext: Attempting logout.");
    setAuthOperationLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('UserContext: Supabase logout error:', error);
        throw error;
      }
      setUser(null);
      setPlan('free');
      setUsage(JSON.parse(localStorage.getItem('guestUsage') || '{}'));
      setTokenCache(null);
      setIsGuest(true);
      console.log("UserContext: Logout successful.");
    } catch (error) {
      console.error('UserContext: Logout error:', error);
      throw error;
    } finally {
      setAuthOperationLoading(false);
    }
  };

  const getToken = async () => {
    console.log("UserContext: Getting token...");
    if (tokenCache) {
      console.log("UserContext: Returning cached token.");
      return tokenCache;
    }
    if (isGuest) {
      console.log("UserContext: Guest mode, no auth token required.");
      return null; // Guest requests don't need auth token
    }

    const maxRetries = 3;
    let attempt = 1;

    while (attempt <= maxRetries) {
      try {
        console.log(`UserContext: Token fetch attempt ${attempt}/${maxRetries}`);
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error('UserContext: Error in getToken:', error);
          throw error;
        }
        const token = data.session?.access_token;
        setTokenCache(token || null);
        console.log(token ? "UserContext: Token obtained." : "UserContext: No token available.");
        return token || null;
      } catch (error) {
        console.error(`UserContext: Token fetch attempt ${attempt} failed:`, error);
        if (attempt === maxRetries) {
          setTokenCache(null);
          return null;
        }
        attempt++;
        await new Promise(resolve => setTimeout(resolve, 1000 * (2 ** attempt)));
      }
    }
  };

  const updatePlan = (newPlan) => {
    setPlan(newPlan);
    console.log("UserContext: Plan updated to:", newPlan);
  };

  const updateUsage = (feature, count, resetAt) => {
    setUsage(prevUsage => {
      const newUsage = {
        ...prevUsage,
        [feature]: { used_count: count, reset_at: resetAt || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() }
      };
      if (isGuest) {
        localStorage.setItem('guestUsage', JSON.stringify(newUsage));
      }
      console.log(`UserContext: Usage for ${feature} updated: ${count}`);
      return newUsage;
    });
  };

  return (
    <UserContext.Provider value={{
      user, plan, usage, initialLoading, authOperationLoading, isGuest, logout, getToken, updatePlan, updateUsage, handleGoogleSignIn, GUEST_LIMITS
    }}>
      {children}
    </UserContext.Provider>
  );
};